/**
 * A máquina de estado do campo de pergunta — fora do componente, de propósito.
 *
 * Os três campos (Transações, Projeções/Categorias, e o de análise no topo)
 * passam a compartilhar uma casca só. Migrar dois componentes que JÁ FUNCIONAM
 * é o risco real desta entrega, e ele não é visual: é o Enter parar de enviar,
 * o spinner não sumir, o erro não limpar quando se redigita. Nada disso quebra
 * o build.
 *
 * Como função pura, isso é testável com o jest que o `react-scripts` já traz —
 * sem instalar biblioteca de renderização, no mesmo estilo dos 427 testes do
 * backend. Era essa a decisão D10.
 */

export interface AskState<R> {
  texto: string;
  carregando: boolean;
  erro: string | null;
  resultado: R | null;
}

export type AskAction<R> =
  | { type: 'digitar'; texto: string }
  | { type: 'enviar' }
  | { type: 'sucesso'; resultado: R }
  | { type: 'falha'; erro: string }
  | { type: 'fecharErro' }
  | { type: 'focar' }
  | { type: 'limpar' };

/**
 * O aviso sai por INTERAÇÃO, nunca por relógio.
 *
 * ── As duas queixas, e por que não se contradizem ─────────────────────────
 *
 * "esse erro que não some mais" veio de um aviso que ficava mesmo depois de
 * ela recomeçar. A correção foi um timer de 10s — e a queixa seguinte foi o
 * oposto: "sumiu, mas o aviso tem que ficar até eu clicar no input de novo".
 *
 * As duas dizem a MESMA coisa: o aviso tem que acompanhar o que ela está
 * fazendo, e relógio não sabe o que ela está fazendo. Some quando ela volta
 * ao campo para perguntar de novo; fica enquanto ela está lendo.
 *
 * Três saídas, todas ligadas a um gesto dela: focar o campo, digitar, ou o X.
 */

export const estadoInicial = <R>(): AskState<R> => ({
  texto: '',
  carregando: false,
  erro: null,
  resultado: null,
});

/** Texto em branco ou requisição em voo não enviam. */
export const podeEnviar = <R>(s: AskState<R>): boolean =>
  s.texto.trim().length > 0 && !s.carregando;

export const askReducer = <R>(s: AskState<R>, a: AskAction<R>): AskState<R> => {
  switch (a.type) {
    case 'digitar':
      // Voltar a digitar descarta a resposta anterior. Manter o resultado velho
      // na tela enquanto ela escreve outra pergunta é a resposta certa para a
      // pergunta errada, que é o modo de falha que este campo inteiro evita.
      return { ...s, texto: a.texto, erro: null, resultado: null };

    case 'enviar':
      if (!podeEnviar(s)) return s;
      return { ...s, carregando: true, erro: null, resultado: null };

    case 'sucesso':
      return { ...s, carregando: false, erro: null, resultado: a.resultado };

    case 'falha':
      return { ...s, carregando: false, erro: a.erro, resultado: null };

    case 'focar':
      // Voltar ao campo é começar de novo. É a saída que ela pediu, e a que
      // um timer nunca acerta: ela pode passar um minuto lendo a resposta
      // anterior antes de voltar.
      return s.erro ? { ...s, erro: null } : s;

    case 'fecharErro':
      // Fecha SÓ o aviso e preserva o texto. Quem fecha um erro quase sempre
      // vai reescrever a pergunta, e apagar o que ela digitou junto com o
      // aviso obrigaria a redigitar tudo — o oposto de tirar atrito.
      return { ...s, erro: null };

    case 'limpar':
      return estadoInicial<R>();
  }
};
